import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, Loader2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";
import { money } from "../lib/helpers";
import { PageHeader } from "../components/Common";
import { Button } from "../components/ui/button";

export default function Billing() {
  const { user, setUser } = useApp();
  const [params, setParams] = useSearchParams();
  const [b, setB] = useState(null);
  const [checking, setChecking] = useState(!!params.get("session_id"));

  const load = () => api.get("/billing").then((r) => setB(r.data)).catch(() => setB({ forbidden: true }));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let tries = 0, timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.status === "complete") {
          toast.success("Subscription active. Thank you!");
          const me = await api.get("/auth/me");
          setUser(me.data); setChecking(false); setParams({}); load();
          return;
        }
      } catch (e) { /* retry */ }
      if (++tries < 10) timer = setTimeout(poll, 2000); else setChecking(false);
    };
    poll();
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const go = async (path) => {
    try {
      const { data } = await api.post(path, { origin_url: window.location.origin });
      window.location.href = data.checkout_url || data.url;
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!b) return null;
  if (b.forbidden) return <p className="text-muted-foreground" data-testid="billing-owner-only">Only the clinic owner can manage billing. Please contact your clinic owner.</p>;
  const days = b.trial_ends_at ? Math.max(Math.ceil((new Date(b.trial_ends_at) - new Date()) / 86400000), 0) : 0;

  return (
    <div className="max-w-2xl">
      <PageHeader title="Billing" eyebrow={user.clinic.name} />
      {checking && <div className="flex items-center gap-2 mb-6 text-sm" data-testid="billing-checking"><Loader2 className="h-4 w-4 animate-spin" />Confirming your subscription…</div>}
      <div className="rounded-[var(--radius)] border bg-card p-8 space-y-6" data-testid="billing-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{b.plan_name}</p>
            <p className="font-display text-4xl font-bold mt-2">{money(b.plan_amount, (b.plan_currency || "eur").toUpperCase())}<span className="text-base font-normal text-muted-foreground"> / month</span></p>
          </div>
          <span data-testid="billing-status" className={`rounded-full px-3 py-1 text-xs font-semibold ${b.is_active ? "bg-emerald-500/15 text-emerald-800" : "bg-red-500/15 text-red-700"}`}>
            {b.plan === "internal" ? "Included" : b.plan === "free" ? "Free access" : b.status === "trial" ? `Trial · ${days} days left` : b.status}
          </span>
        </div>
        <ul className="text-sm space-y-2 text-muted-foreground">
          {["Unlimited locations, currencies and staff", "Online booking widget & Google booking link", "Patient app, consent forms with e-signature", "Email & WhatsApp confirmations and reminders"].map((x) => (
            <li key={x} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" />{x}</li>
          ))}
        </ul>
        {b.plan === "free" && <p className="text-sm text-emerald-800" data-testid="billing-free-msg">Free access: no payment needed.</p>}
        {!["internal", "free"].includes(b.plan) && (
          <div className="flex flex-wrap gap-3">
            {b.status !== "active" && <Button className="rounded-full" data-testid="billing-subscribe-btn" onClick={() => go("/billing/checkout")}><CreditCard className="h-4 w-4 mr-2" />Subscribe</Button>}
            {b.has_subscription && <Button variant="outline" className="rounded-full" data-testid="billing-portal-btn" onClick={() => go("/billing/portal")}>Manage billing</Button>}
          </div>
        )}
        {!b.is_active && <p className="text-sm text-destructive" data-testid="billing-inactive-msg">Your clinic is paused until a subscription is active. Your data is safe.</p>}
      </div>
    </div>
  );
}
